import { useState, useEffect } from "react";
import { Check, Plus, Trash2, X, Flag } from "lucide-react";

const categories = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

export default function TaskEditDialog({ task, onClose, onSave, onDelete, onAddSubtask, onToggleSubtask, onDeleteSubtask, onToggleUrgent }) {
  const [name, setName] = useState(task.name);
  const [notes, setNotes] = useState(task.notes || "");
  const [category, setCategory] = useState(task.category);
  const [newSubtaskName, setNewSubtaskName] = useState("");
  const isUrgent = task.urgent || false;

  const subtasks = task.subtasks || [];
  const completedCount = subtasks.filter((s) => s.completed).length;
  const totalCount = subtasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleSave = () => {
    const updates = {};
    if (name.trim() !== task.name) updates.name = name.trim();
    if (notes !== (task.notes || "")) updates.notes = notes;
    if (category !== task.category) updates.category = category;
    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      onClose();
    }
  };

  const handleAddSubtask = () => {
    const trimmed = newSubtaskName.trim();
    if (!trimmed) return;
    onAddSubtask(task.id, trimmed);
    setNewSubtaskName("");
  };

  const handleSubtaskKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSubtask();
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-title-row">
          <div className="dialog-title">Edit Task</div>
          {onToggleUrgent && (
            <button
              className={`dialog-urgent-toggle ${isUrgent ? "active" : ""}`}
              onClick={() => onToggleUrgent(task.id)}
              title={isUrgent ? "Remove urgent flag" : "Mark as urgent"}
              aria-label={isUrgent ? "Remove urgent flag" : "Mark as urgent"}
              data-testid="dialog-urgent-toggle"
            >
              <Flag size={14} />
              <span>{isUrgent ? "Urgent" : "Flag Urgent"}</span>
            </button>
          )}
        </div>

        <div className="dialog-field">
          <label className="dialog-label" htmlFor="edit-task-name">Name</label>
          <input
            className="dialog-input"
            id="edit-task-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            data-testid="task-edit-name-input"
          />
        </div>

        <div className="dialog-field">
          <label className="dialog-label" htmlFor="edit-task-notes">Notes</label>
          <textarea
            className="dialog-textarea"
            id="edit-task-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes..."
            data-testid="task-notes-textarea"
          />
        </div>

        <div className="dialog-field">
          <label className="dialog-label">Category</label>
          <div className="category-selector">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-btn ${category === cat.id ? `active-${cat.id}` : ""}`}
                onClick={() => setCategory(cat.id)}
                type="button"
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sub-tasks Section */}
        <div className="dialog-field">
          <label className="dialog-label">
            Sub-tasks
            {totalCount > 0 && (
              <span className="subtask-progress-label">{completedCount}/{totalCount}</span>
            )}
          </label>

          {/* Progress bar */}
          {totalCount > 0 && (
            <div className="subtask-progress-bar">
              <div
                className="subtask-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Subtask list */}
          <div className="subtask-list" data-testid="subtask-list">
            {subtasks.map((st) => (
              <div key={st.id} className="subtask-item">
                <button
                  className={`subtask-checkbox ${st.completed ? "checked" : ""}`}
                  onClick={() => onToggleSubtask(task.id, st.id)}
                  data-testid="subtask-toggle"
                >
                  {st.completed && <Check size={10} />}
                </button>
                <span className={`subtask-name ${st.completed ? "completed" : ""}`}>
                  {st.name}
                </span>
                <button
                  className="subtask-delete-btn"
                  onClick={() => onDeleteSubtask(task.id, st.id)}
                  data-testid="subtask-delete"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>

          {/* Add subtask input */}
          <div className="subtask-add-row">
            <input
              className="subtask-add-input"
              placeholder="Add a sub-task..."
              value={newSubtaskName}
              onChange={(e) => setNewSubtaskName(e.target.value)}
              onKeyDown={handleSubtaskKeyDown}
              data-testid="subtask-input"
            />
            <button
              className="subtask-add-btn"
              onClick={handleAddSubtask}
              disabled={!newSubtaskName.trim()}
              data-testid="subtask-add-btn"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="dialog-actions">
          <button className="dialog-btn dialog-btn-danger" onClick={onDelete} data-testid="task-delete-button">
            Delete
          </button>
          <button className="dialog-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="dialog-btn dialog-btn-primary" onClick={handleSave} data-testid="task-save-button">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
